import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadFile, errMsg } from "@/lib/hsUtil";

const FIELDS = [
  ["supervisor_name", "Site supervisor"], ["supervisor_phone", "Supervisor phone"],
  ["emergency_signal", "Emergency warning signal"], ["assembly_point", "Assembly point"],
  ["first_aid", "First aid kit location"], ["fire_extinguisher", "Fire extinguisher location"],
  ["medical_centre", "Nearest medical centre"],
];

const SiteField = ({ k, label, value, onChange }) => (
  <label className="block">
    <span className="text-xs text-silver">{label}</span>
    <input className="input w-full" value={value} onChange={e => onChange(k, e.target.value)} data-testid={`site-field-${k}`}/>
  </label>
);

const HazardPicker = ({ options, picked, onToggle }) => (
  <div className="space-y-1 max-h-96 overflow-y-auto" data-testid="site-hazard-picker">
    {options.length === 0 && <div className="opacity-60 text-sm">No hazards selected for this project yet. Set them up in H&amp;S → Project hazards, then pick the top 8 here.</div>}
    {options.map(o => {
      const on = picked.includes(o.key);
      return (
        <label key={o.key} className={`flex gap-2 p-2 rounded border text-sm cursor-pointer ${on ? "border-silver" : "border-neutral-800"}`}>
          <input type="checkbox" checked={on} disabled={!on && picked.length >= 8} onChange={() => onToggle(o.key)} data-testid={`site-hazard-${o.key}`}/>
          <span><span className="text-xs opacity-60">{o.source}{o.ref_id ? ` ${o.ref_id}` : ""} · </span>{o.hazard}
            {o.controls && <span className="block text-xs opacity-70">Controls: {o.controls}</span>}</span>
        </label>
      );
    })}
  </div>
);

export default function SiteEditor({ pid, onClose }) {
  const [d, setD] = useState(null);
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/hs/sites/${pid}`).then(r => { if (live) { setD(r.data); setSite(r.data.site); } });
    return () => { live = false; };
  }, [pid]);
  if (!d) return <div className="opacity-70">Loading…</div>;
  const setField = (k, v) => setSite(s => ({ ...s, [k]: v }));
  const toggle = key => setSite(s => ({ ...s, top_hazards: s.top_hazards.includes(key) ? s.top_hazards.filter(x => x !== key) : [...s.top_hazards, key] }));
  async function save() {
    setSaving(true);
    try { const r = await api.put(`/hs/sites/${pid}`, site); setSite(r.data); toast.success("Site details saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }
  const p = d.project;
  const pdf = () => downloadFile(`/hs/sites/${pid}/sign.pdf`, `site_sign_${p.site_code}.pdf`, { base: window.location.origin })
    .catch(e => toast.error(errMsg(e, "Could not build the PDF")));
  return (
    <div className="space-y-4" data-testid="hs-site-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-ghost text-sm" onClick={onClose} data-testid="site-editor-back">Back to sites</button>
        <h2 className="text-lg">{p.name}</h2>
      </div>
      <div className="card flex flex-wrap items-center gap-4">
        <div><div className="text-xs text-silver">6 digit site code</div>
          <div className="text-3xl font-mono tracking-[0.3em]" data-testid="site-editor-code">{p.site_code}</div></div>
        <button className="btn-silver" onClick={pdf} data-testid="site-sign-pdf-btn">Download QR sign PDF</button>
        <a className="btn-ghost" href={`/qr/${p.qr_code}`} target="_blank" rel="noreferrer" data-testid="site-open-public-link">Open sign-in page</a>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card space-y-3">
          <h3 className="text-silver">Site details</h3>
          {FIELDS.map(([k, label]) => <SiteField key={k} k={k} label={label} value={site[k] || ""} onChange={setField}/>)}
        </div>
        <div className="card space-y-3">
          <h3 className="text-silver">Top hazards <span className="text-xs opacity-60" data-testid="site-hazard-count">({site.top_hazards.length} of 8)</span></h3>
          <HazardPicker options={d.hazard_options} picked={site.top_hazards} onToggle={toggle}/>
        </div>
      </div>
      <button className="btn-silver" disabled={saving} onClick={save} data-testid="site-save-btn">{saving ? "Saving…" : "Save site details"}</button>
    </div>
  );
}
